import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Collapse,
  Switch,
  Tooltip,
  Link,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  MergeType as MergeIcon,
} from "@mui/icons-material";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  CreateBrandDialog,
  AddSupplierDialog,
  MergeDialog,
  FeedbackSnackbar,
  type SnackbarState,
} from "./MarcasDialogs";

interface BrandSupplier {
  id: number;
  name: string;
  productsCount: number;
}
interface Brand {
  id: number;
  name: string;
  active: boolean;
  suppliersCount: number;
  productsCount: number;
  suppliers: BrandSupplier[];
}

const API_BASE = "/api/brands";

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token)
      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
  } catch {
    // fall through
  }
  return { "Content-Type": "application/json" };
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(url, {
    ...options,
    headers: { ...headers, ...options?.headers },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
  return res.json();
}

const showSuccess = (msg: string): SnackbarState => ({
  open: true,
  message: msg,
  severity: "success",
});
const showError = (msg: string): SnackbarState => ({
  open: true,
  message: msg,
  severity: "error",
});

export default function MarcasPage(): React.ReactElement {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [creating, setCreating] = useState(false);
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [addSupplierBrandId, setAddSupplierBrandId] = useState<number | null>(
    null,
  );
  const [supplierName, setSupplierName] = useState("");
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [merging, setMerging] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = debouncedSearch
        ? `?search=${encodeURIComponent(debouncedSearch)}`
        : "";
      const data = await apiFetch<Brand[]>(`${API_BASE}${params}`);
      setBrands(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || "Error al cargar marcas");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(h);
  }, [searchTerm]);

  const toggleExpand = (id: number) =>
    setExpandedId((p) => (p === id ? null : id));

  const handleCreateBrand = async () => {
    if (!newBrandName.trim()) return;
    try {
      setCreating(true);
      await apiFetch(`${API_BASE}`, {
        method: "POST",
        body: JSON.stringify({ name: newBrandName.trim() }),
      });
      setSnackbar(showSuccess("Marca creada correctamente"));
      setCreateOpen(false);
      setNewBrandName("");
      fetchBrands();
    } catch (err: any) {
      setSnackbar(showError(err.message || "Error al crear marca"));
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (brand: Brand) => {
    try {
      await apiFetch(`${API_BASE}/${brand.id}`, {
        method: "PUT",
        body: JSON.stringify({ active: !brand.active }),
      });
      setBrands((prev) =>
        prev.map((b) => (b.id === brand.id ? { ...b, active: !b.active } : b)),
      );
      setSnackbar(
        showSuccess(`Marca ${!brand.active ? "activada" : "desactivada"}`),
      );
    } catch (err: any) {
      setSnackbar(showError(err.message || "Error al actualizar marca"));
    }
  };

  const handleAddSupplier = async () => {
    if (!supplierName.trim() || !addSupplierBrandId) return;
    try {
      setAddingSupplier(true);
      await apiFetch(`${API_BASE}/${addSupplierBrandId}/suppliers`, {
        method: "POST",
        body: JSON.stringify({ name: supplierName.trim() }),
      });
      setSnackbar(showSuccess("Proveedor agregado correctamente"));
      setAddSupplierOpen(false);
      setSupplierName("");
      setAddSupplierBrandId(null);
      fetchBrands();
    } catch (err: any) {
      setSnackbar(showError(err.message || "Error al agregar proveedor"));
    } finally {
      setAddingSupplier(false);
    }
  };

  const handleRemoveSupplier = async (brandId: number, supplierId: number) => {
    try {
      await apiFetch(`${API_BASE}/${brandId}/suppliers/${supplierId}`, {
        method: "DELETE",
      });
      setSnackbar(showSuccess("Proveedor removido"));
      fetchBrands();
    } catch (err: any) {
      setSnackbar(showError(err.message || "Error al remover proveedor"));
    }
  };
  const handleMerge = async () => {
    if (!mergeTargetId || !mergeSourceId) return;
    try {
      setMerging(true);
      await apiFetch(`${API_BASE}/${mergeTargetId}/merge/${mergeSourceId}`, {
        method: "POST",
      });
      setSnackbar(showSuccess("Marcas fusionadas correctamente"));
      setMergeOpen(false);
      setMergeSourceId("");
      setMergeTargetId(null);
      fetchBrands();
    } catch (err: any) {
      setSnackbar(showError(err.message || "Error al fusionar marcas"));
    } finally {
      setMerging(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Cargando marcas...
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h4" component="h1">
          Gestion de Marcas
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Crear marca
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" alignItems="center" spacing={1}>
        <TextField
          size="small"
          placeholder="Buscar marca..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ maxWidth: 400 }}
        />
        <Typography variant="body2" color="text.secondary">
          {brands.length} marcas
        </Typography>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Nombre</TableCell>
              <TableCell align="center">Proveedores</TableCell>
              <TableCell align="center">Productos</TableCell>
              <TableCell align="center">Activa</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    No se encontraron marcas
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              brands.map((brand) => (
                <React.Fragment key={brand.id}>
                  <TableRow
                    hover
                    sx={{
                      "& > *": {
                        borderBottom:
                          expandedId === brand.id ? "unset" : undefined,
                      },
                    }}
                  >
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(brand.id)}
                      >
                        {expandedId === brand.id ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => toggleExpand(brand.id)}
                        sx={{ fontWeight: 500, cursor: "pointer" }}
                      >
                        {brand.name}
                      </Link>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={brand.suppliersCount}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={brand.productsCount}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={brand.active}
                        onChange={() => handleToggleActive(brand)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Fusionar otra marca en esta">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setMergeTargetId(brand.id);
                            setMergeOpen(true);
                          }}
                        >
                          <MergeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={6}>
                      <Collapse
                        in={expandedId === brand.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Stack spacing={1} sx={{ py: 2, pl: 6 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="subtitle2">
                              Proveedores de {brand.name}
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                setAddSupplierBrandId(brand.id);
                                setAddSupplierOpen(true);
                              }}
                            >
                              Agregar proveedor
                            </Button>
                          </Stack>
                          {brand.suppliers && brand.suppliers.length > 0 ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Proveedor</TableCell>
                                  <TableCell align="center">
                                    Productos
                                  </TableCell>
                                  <TableCell align="center">Acciones</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {brand.suppliers.map((sup) => (
                                  <TableRow key={sup.id}>
                                    <TableCell>{sup.name}</TableCell>
                                    <TableCell align="center">
                                      {sup.productsCount}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="Remover proveedor de esta marca">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleRemoveSupplier(
                                              brand.id,
                                              sup.id,
                                            )
                                          }
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sin proveedores asignados
                            </Typography>
                          )}
                        </Stack>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateBrandDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        brandName={newBrandName}
        onBrandNameChange={setNewBrandName}
        onSubmit={handleCreateBrand}
        creating={creating}
      />
      <AddSupplierDialog
        open={addSupplierOpen}
        onClose={() => setAddSupplierOpen(false)}
        supplierName={supplierName}
        onSupplierNameChange={setSupplierName}
        onSubmit={handleAddSupplier}
        adding={addingSupplier}
      />
      <MergeDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        sourceId={mergeSourceId}
        onSourceIdChange={setMergeSourceId}
        onSubmit={handleMerge}
        merging={merging}
      />
      <FeedbackSnackbar
        snackbar={snackbar}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Stack>
  );
}
